/**
 * IV Smile / Skew Curve — IV vs Strike for current expiry
 */
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Legend } from 'recharts';
import { Box, Typography } from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';

interface Props {
  data: Array<{ strike: number; ceIv?: number; peIv?: number }>;
  spotPrice: number;
}

const IVSmileChart: React.FC<Props> = ({ data, spotPrice }) => {
  const { C } = useAppTheme();

  if (!data || data.length === 0) return null;

  const sorted = data
    .filter(r => r.ceIv || r.peIv)
    .sort((a, b) => a.strike - b.strike)
    .map(r => ({
      strike: r.strike,
      'CE IV': r.ceIv ? Number(r.ceIv).toFixed(2) : null,
      'PE IV': r.peIv ? Number(r.peIv).toFixed(2) : null
    }));

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>IV Smile / Skew</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
        Steep put skew = fear. Flattening skew = complacency.
      </Typography>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={sorted} margin={{ top: 5, right: 10, left: 0, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} opacity={0.4} />
          <XAxis dataKey="strike" angle={-45} textAnchor="end" tick={{ fontSize: 10 }} />
          <YAxis tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} />
          <Tooltip
            formatter={(v: any) => [`${v}%`]}
            labelFormatter={l => `Strike: ${l}`}
            contentStyle={{ backgroundColor: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }}
          />
          <Legend />
          <ReferenceLine x={spotPrice} stroke={C.spot} strokeDasharray="4 4" />
          <Line type="monotone" dataKey="CE IV" stroke={C.callText} dot={false} strokeWidth={2} connectNulls />
          <Line type="monotone" dataKey="PE IV" stroke={C.putText} dot={false} strokeWidth={2} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default IVSmileChart;
